import { memo, forwardRef, useState, useRef, useImperativeHandle, useCallback, useEffect } from 'react';
import { v4 } from 'uuid';
import { useFocusHandling, ControlContainer, InvisibleSelector, DecorativeContainer } from './base.js';
export { SelectorStateClassnames } from './base.js';
import { CheckboxStyles } from './checkbox-styles.js';
import { Label } from './label.js';
import { RadioStyles } from './radio-styles.js';
import { SwitchStyles } from './switch-styles.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const useLabelAndMessageId = id => ({
  labelId: useRef(id ? `${id}_label` : v4()).current,
  messageId: useRef(id ? `${id}_message` : v4()).current
});
const useHandleChange = onChange => useCallback(e => {
  onChange?.(e.target.checked, e);
}, [onChange]);
const useRadioHandleChange = onChange => useCallback(e => {
  onChange?.(e.target.value, e);
}, [onChange]);
const useIndeterminate = (selectorRef, checked, indeterminate) => useEffect(() => {
  if (!checked && selectorRef?.current != null) {
    selectorRef.current.indeterminate = Boolean(indeterminate);
  }
}, [indeterminate, checked]);
const Switch = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    label,
    message,
    className,
    onBlur,
    hideLabel,
    onChange,
    marginBottom,
    marginTop,
    labelPosition = "left",
    ...props
  } = _ref;
  const [noFocus, setNoFocus] = useState(false);
  const selectorRef = useRef(null);
  const {
    handleClick,
    handleBlur
  } = useFocusHandling(selectorRef, setNoFocus, onBlur);
  const {
    labelId,
    messageId
  } = useLabelAndMessageId(props.id);
  const handleChange = useHandleChange(onChange);
  const selectorClassName = noFocus ? "no-focus" : "";
  useImperativeHandle(ref, () => selectorRef.current);
  return /*#__PURE__*/jsxs(ControlContainer, {
    disabled: props.disabled,
    className: className,
    marginBottom: marginBottom,
    marginTop: marginTop,
    onClick: handleClick,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      type: "checkbox",
      onChange: handleChange,
      onBlur: handleBlur,
      className: selectorClassName,
      ref: selectorRef,
      "aria-labelledby": labelId,
      "aria-describedby": message && messageId
    }), /*#__PURE__*/jsx(SwitchStyles, {}), /*#__PURE__*/jsx(Label, {
      label: label,
      message: message,
      labelId: labelId,
      messageId: messageId,
      labelPosition: labelPosition,
      hideLabel: hideLabel
    })]
  });
}));
const RadioButton = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    label,
    message,
    className,
    onBlur,
    error,
    hideLabel,
    onChange,
    marginBottom,
    marginTop,
    labelPosition = "right",
    ...props
  } = _ref2;
  const [noFocus, setNoFocus] = useState(false);
  const selectorRef = useRef(null);
  const {
    handleClick,
    handleBlur
  } = useFocusHandling(selectorRef, setNoFocus, onBlur);
  const {
    labelId,
    messageId
  } = useLabelAndMessageId(props.id);
  const handleChange = useRadioHandleChange(onChange);
  const selectorClassName = noFocus ? "no-focus" : "";
  useImperativeHandle(ref, () => selectorRef.current);
  return /*#__PURE__*/jsxs(ControlContainer, {
    disabled: props.disabled,
    className: className,
    marginBottom: marginBottom,
    marginTop: marginTop,
    onClick: handleClick,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      type: "radio",
      onBlur: handleBlur,
      onChange: handleChange,
      className: selectorClassName,
      ref: selectorRef,
      "aria-invalid": error,
      "aria-labelledby": labelId,
      "aria-describedby": message && messageId
    }), /*#__PURE__*/jsx(RadioStyles, {}), /*#__PURE__*/jsx(Label, {
      label: label,
      message: message,
      labelId: labelId,
      messageId: messageId,
      labelPosition: labelPosition,
      hideLabel: hideLabel
    })]
  });
}));
const Checkbox = /*#__PURE__*/memo( /*#__PURE__*/forwardRef((_ref3, ref) => {
  let {
    label,
    message,
    className,
    onBlur,
    indeterminate,
    error,
    hideLabel,
    onChange,
    marginBottom,
    marginTop,
    disabled,
    loading,
    labelPosition = "right",
    ...props
  } = _ref3;
  const [noFocus, setNoFocus] = useState(false);
  const {
    labelId,
    messageId
  } = useLabelAndMessageId(props.id);
  const selectorRef = useRef(null);
  const {
    handleClick,
    handleBlur
  } = useFocusHandling(selectorRef, setNoFocus, onBlur);
  useIndeterminate(selectorRef, props.checked, indeterminate);
  const handleChange = useHandleChange(onChange);
  useImperativeHandle(ref, () => selectorRef.current);
  const selectorClassName = noFocus ? "no-focus" : "";
  return /*#__PURE__*/jsxs(ControlContainer, {
    disabled: disabled,
    loading: loading,
    className: className,
    marginBottom: marginBottom,
    marginTop: marginTop,
    onClick: handleClick,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      "aria-busy": loading,
      disabled: disabled,
      type: "checkbox",
      onBlur: handleBlur,
      onChange: !loading ? handleChange : undefined,
      className: selectorClassName,
      ref: selectorRef,
      "aria-invalid": error,
      "aria-labelledby": labelId,
      "aria-describedby": message && messageId
    }), /*#__PURE__*/jsx(CheckboxStyles, {
      loading: loading,
      checked: props.checked,
      indeterminate: indeterminate
    }), /*#__PURE__*/jsx(Label, {
      label: label,
      message: message,
      labelId: labelId,
      messageId: messageId,
      labelPosition: labelPosition,
      hideLabel: hideLabel
    })]
  });
}));

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a Switch as a visual aid. For a functional alternative, use
 * the normal Switch component.
 */
const SwitchDecorative = /*#__PURE__*/memo(_ref4 => {
  let {
    className,
    ...props
  } = _ref4;
  return /*#__PURE__*/jsxs(DecorativeContainer, {
    className: className,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      type: "checkbox",
      readOnly: true,
      "aria-hidden": true,
      tabIndex: -1
    }), /*#__PURE__*/jsx(SwitchStyles, {})]
  });
});

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a RadioButton as a visual aid. For a functional alternative, use
 * the normal RadioButton component.
 */
const RadioButtonDecorative = /*#__PURE__*/memo(_ref5 => {
  let {
    className,
    ...props
  } = _ref5;
  return /*#__PURE__*/jsxs(DecorativeContainer, {
    className: className,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      type: "radio",
      readOnly: true,
      "aria-hidden": true,
      tabIndex: -1
    }), /*#__PURE__*/jsx(RadioStyles, {})]
  });
});

/**
 * This is a purely decorative component. It is only meant as a building block to help create
 * other components that contain a Checkbox as a visual aid. For a functional alternative, use
 * the normal Checkbox component.
 */
const CheckboxDecorative = /*#__PURE__*/memo(_ref6 => {
  let {
    className,
    indeterminate,
    ...props
  } = _ref6;
  const selectorRef = useRef(null);
  useIndeterminate(selectorRef, props.checked, indeterminate);
  return /*#__PURE__*/jsxs(DecorativeContainer, {
    className: className,
    children: [/*#__PURE__*/jsx(InvisibleSelector, {
      ...props,
      ref: selectorRef,
      type: "checkbox",
      readOnly: true,
      "aria-hidden": true,
      tabIndex: -1
    }), /*#__PURE__*/jsx(CheckboxStyles, {
      checked: props.checked,
      indeterminate: indeterminate
    })]
  });
});

export { Checkbox, CheckboxDecorative, RadioButton, RadioButtonDecorative, Switch, SwitchDecorative };
//# sourceMappingURL=index.js.map
